import React, { useMemo } from 'react';
import { Box, Grid, Inscription, LinkButton, Toggle, useResponsive } from '@common-fe/common-fe';

import { ProfileSettingsOption } from '@/modules/user/components/ManageProfile/components/ProfileSettingCard/profileSettings.types';

import ProfileSettingOption from './ProfileSettingOption';

interface Props {
  testId?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  options?: ProfileSettingsOption[];
  isActive?: boolean;
  isActivationDisabled?: boolean;
  onActivate?: (value: boolean) => void;
  settingPropertyName?: string;
  onChangeSettingProperty?: () => void;
  isMobileSettingProperty?: boolean;
}

const ProfileSettingCard: React.FC<Props> = ({
  testId,
  icon,
  title,
  description,
  options,
  isActive,
  isActivationDisabled,
  onActivate,
  settingPropertyName,
  onChangeSettingProperty,
  isMobileSettingProperty,
}) => {
  const { isMobile } = useResponsive();
  const hasOptions = useMemo(() => !!options?.length, [options]);

  return (
    <Box
      round="container1Round"
      pad={isMobile ? 'spacing16' : 'spacing24'}
      background="canvas"
      border={{ color: 'border2', size: '1px' }}
    >
      <Box direction="row" align="center">
        <Box>{icon}</Box>
        <Box margin={{ left: 'spacing16' }} direction="column" flex>
          <Inscription color="text" size="large" weight={700}>
            {title}
          </Inscription>
          <Inscription color="textSecondary" size="small" weight={500}>
            {description}
          </Inscription>
        </Box>
        {onActivate && (
          <Box margin={{ left: 'spacing16' }}>
            <Toggle
              testId={testId}
              value={!!isActive}
              disabled={isActivationDisabled}
              onChange={onActivate}
              style={{
                borderRadius: '24px',
              }}
            />
          </Box>
        )}
      </Box>

      {hasOptions && (
        <Grid margin={{ top: 'spacing24' }} columns={isMobile ? ['1fr'] : ['1fr', '1fr']} gap="spacing16">
          {options?.map((option) => (
            <Box key={option.key}>
              <ProfileSettingOption
                key={option.key}
                title={option.title}
                value={option.value}
                isSelected={option?.isSelected && !!option?.value}
                isActive={option?.isActive && !!option?.value}
                onClick={option.onClick}
              />
              {onChangeSettingProperty && isMobile && option.isEditButton && (
                <Box width="fit-content" margin={{ top: 'spacing6' }}>
                  <LinkButton onClick={onChangeSettingProperty} linkInlineMode>
                    {settingPropertyName}
                  </LinkButton>
                </Box>
              )}
            </Box>
          ))}
        </Grid>
      )}

      {onChangeSettingProperty && (!isMobile || isMobileSettingProperty) && (
        <Box margin={{ top: hasOptions ? 'spacing8' : 'spacing24' }} width="fit-content">
          <LinkButton onClick={onChangeSettingProperty} linkInlineMode>
            {settingPropertyName}
          </LinkButton>
        </Box>
      )}
    </Box>
  );
};

export default ProfileSettingCard;
